"use client";

import { useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight } from "lucide-react";
function MarkerStroke() {
  return <svg aria-hidden="true" viewBox="0 0 220 22" preserveAspectRatio="none" className="pointer-events-none absolute left-0 right-0 -bottom-1 h-[0.35em] w-full" style={{
    color: "var(--primary)"
  }}>
      <path d="M2 13 C 40 4, 90 18, 150 9 S 210 14, 218 8" stroke="currentColor" strokeWidth="8" strokeLinecap="round" fill="none" opacity="0.45" />
    </svg>;
}
function StoriesGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [showAll, setShowAll] = useState(false);
  if (loading) {
    return <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 md:gap-8">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="animate-pulse rounded-lg border border-border/60 bg-card p-4">
            <div className="rounded-lg aspect-[16/10] w-full" style={{
          backgroundColor: "var(--muted)"
        }} />
            <div className="pt-5 space-y-3">
              <div className="flex gap-2">
                <div className="h-4 w-20 rounded-lg" style={{
              backgroundColor: "var(--muted)"
            }} />
                <div className="h-4 w-16 rounded-lg" style={{
              backgroundColor: "var(--muted)"
            }} />
              </div>
              <div className="h-6 w-full rounded" style={{
            backgroundColor: "var(--muted)"
          }} />
              <div className="h-4 w-5/6 rounded" style={{
            backgroundColor: "var(--muted)"
          }} />
              <div className="h-3 w-1/2 rounded pt-2" style={{
            backgroundColor: "var(--muted)"
          }} />
            </div>
          </div>)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <div className="mt-16">
        <p style={{
        color: "var(--muted-foreground)"
      }} className="font-sans text-base">
          No articles available yet.
        </p>
      </div>;
  }
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return "";
    }
  };
  const stripHtml = (html?: string) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").trim();
  };
  const visiblePosts = showAll ? posts : posts.slice(0, 6);
  return <div className="mt-12 md:mt-16">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {visiblePosts.map((post, i) => {
        const excerpt = stripHtml(post.excerpt);
        const truncatedExcerpt = excerpt.length > 130 ? excerpt.slice(0, 130).trim() + "…" : excerpt;
        const category = post.categories && post.categories.length > 0 ? post.categories[0] : null;
        const wordCount = stripHtml(post.content).split(/\s+/).filter(Boolean).length;
        const readTime = Math.max(2, Math.ceil(wordCount / 220));
        return <article key={post.id} data-index={i} className="group flex flex-col justify-between overflow-hidden rounded-lg border border-border/80 bg-card p-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md">
              <div>
                <Link to={post.link || "#"} className="block overflow-hidden rounded-lg aspect-[16/10] bg-muted relative">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center" style={{
                backgroundColor: "var(--card)"
              }}>
                      <span className="font-serif text-3xl font-bold italic" style={{
                  color: "var(--muted-foreground)"
                }}>
                        YE
                      </span>
                    </div>}
                </Link>

                <div className="pt-5 flex flex-col">
                  <div className="flex flex-wrap items-center gap-2.5 mb-3">
                    {category && <span className="inline-flex items-center rounded-lg border px-2.5 py-0.5 font-sans text-[13px] font-semibold uppercase tracking-wider" style={{
                  borderColor: "var(--secondary)",
                  backgroundColor: "var(--secondary)",
                  color: "var(--primary)"
                }}>
                        {category.name}
                      </span>}
                    <span className="inline-flex items-center gap-1 font-sans text-xs" style={{
                  color: "var(--muted-foreground)"
                }}>
                      <Clock className="size-3.5" />
                      {readTime} min read
                    </span>
                  </div>

                  <h3 className="font-serif font-semibold transition-colors duration-200 group-hover:text-primary" style={{
                color: "var(--foreground)",
                fontSize: "1.5rem",
                lineHeight: "1.25",
                letterSpacing: "-0.02em"
              }}>
                    <Link to={post.link || "#"} className="inline-block">
                      {post.title}
                    </Link>
                  </h3>

                  {truncatedExcerpt && <p className="mt-3 font-sans line-clamp-3" style={{
                color: "var(--muted-foreground)",
                fontSize: "1.0625rem",
                lineHeight: "1.6"
              }}>
                      {truncatedExcerpt}
                    </p>}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-border/60 flex items-center justify-between font-sans text-xs" style={{
            color: "var(--muted-foreground)"
          }}>
                <div className="flex items-center gap-1.5 truncate">
                  <span>{post.author?.name ? `By ${post.author.name}` : "By Yassine Essaadi"}</span>
                  {post.date && <span aria-hidden="true">·</span>}
                  {post.date && <time dateTime={post.date}>{formatDate(post.date)}</time>}
                </div>

                <Link to={post.link || "#"} className="inline-flex items-center gap-1 font-medium transition-colors hover:text-primary shrink-0" style={{
              color: "var(--primary)"
            }}>
                  <span>Read</span>
                  <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </article>;
      })}
      </div>

      {!showAll && posts.length > 6 && <div className="mt-14 flex justify-center">
          <Button type="button" variant="outline" size="lg" onClick={() => setShowAll(true)} className="rounded-lg px-8 py-6 font-sans text-sm font-semibold tracking-wide border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-200 shadow-sm">
            View All Articles ({posts.length})
          </Button>
        </div>}
    </div>;
}
export default function Insights() {
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 100,
    order: "desc",
    orderby: "date",
    status: ["publish"]
  });
  return <section data-section-id="30641" id="seo-insights-updates" className="w-full py-24 md:py-32 bg-background text-foreground">
      <div className="mx-auto w-full" style={{
      maxWidth: "1280px",
      paddingLeft: "clamp(1.25rem, 4vw, 2rem)",
      paddingRight: "clamp(1.25rem, 4vw, 2rem)"
    }}>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end md:gap-8">
          <div className="md:col-span-3">
            <span className="font-sans text-[16px] font-semibold uppercase" style={{
            color: "var(--muted-foreground)",
            letterSpacing: "0.08em"
          }}>
              SEO Insights
            </span>
          </div>

          <div className="md:col-span-9">
            <h2 className="font-serif font-bold" style={{
            color: "var(--foreground)",
            fontSize: "clamp(1.8125rem, 2.8vw, 2.375rem)",
            lineHeight: "1.1",
            letterSpacing: "-0.03em"
          }}>
              <span>{"Modern SEO "}</span>
              <span className="relative inline-block">
                <span className="relative z-10">Updates</span>
                <MarkerStroke />
              </span>
            </h2>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 100,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <StoriesGrid />
        </PostsProvider>
      </div>
    </section>;
}